import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { CalendarClock } from "lucide-react";

// Parse a "YYYY-MM-DDTHH:mm" local string into { date: Date|null, time: "HH:mm" }
function parseValue(value) {
  if (!value) return { date: null, time: "" };
  const [d, t] = value.split("T");
  const [y, mo, da] = (d || "").split("-").map(Number);
  const date = y && mo && da ? new Date(y, mo - 1, da) : null;
  return { date, time: (t || "").slice(0, 5) };
}

function pad(n) { return String(n).padStart(2, "0"); }

function compose(date, time) {
  if (!date) return "";
  const ds = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${ds}T${time || "09:00"}`;
}

function display(value) {
  const { date, time } = parseValue(value);
  if (!date) return "Pick date & time";
  const opts = { day: "2-digit", month: "short", year: "numeric" };
  let label = date.toLocaleDateString("en-GB", opts);
  if (time) {
    const [h, m] = time.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    label += `, ${h12}:${pad(m)} ${ampm}`;
  }
  return label;
}

/**
 * Friendly date + time picker (calendar popover + native time picker) — replaces the
 * raw browser datetime-local input so admins never have to type the time by hand.
 * Emits a "YYYY-MM-DDTHH:mm" local string, identical to datetime-local.
 */
export default function DateTimePicker({ label, value, onChange, testid, hint, optional }) {
  const [open, setOpen] = useState(false);
  const { date, time } = parseValue(value);

  const onDate = (d) => { if (d) onChange(compose(d, time)); };
  const onTime = (e) => onChange(compose(date || new Date(), e.target.value));

  return (
    <div data-testid={testid}>
      {label && (
        <label className="label-mono block mb-1 flex items-center gap-2">
          {label}
          {optional && <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-white/10 text-gray-400 rounded-sm">Optional</span>}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="input-field w-full flex items-center justify-between text-left"
            data-testid={testid ? `${testid}-trigger` : undefined}
          >
            <span className={value ? "text-white" : "text-gray-500"}>{display(value)}</span>
            <CalendarClock className="w-4 h-4 text-brand flex-shrink-0" strokeWidth={1.5} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0E1424] border-white/10" align="start">
          <Calendar mode="single" selected={date || undefined} onSelect={onDate} initialFocus />
          <div className="border-t border-white/10 p-3 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
            <input
              type="time"
              value={time}
              onChange={onTime}
              className="input-field flex-1"
              data-testid={testid ? `${testid}-time` : undefined}
            />
            <button type="button" onClick={() => setOpen(false)} className="btn-primary px-4 py-2 text-xs">Done</button>
          </div>
        </PopoverContent>
      </Popover>
      {hint && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}
